import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Employee } from '../types';
import TableLoadingView from '@/components/tables/TableLoadingView';
import EmptyTableView from '@/components/tables/EmptyTableView';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import Table, { TableHeader, TableBody } from '@/components/tables/TableBasics';
import {
  CheckboxCell,
  CheckboxHeader,
  TextCell,
  TextWithHoverAction,
  ToggleCell,
  InviteLinkCell,
  LastOnlineCell,
} from '@/components/tables/contentFields';
import { useGlobalUserRoles } from '../hooks/useEmployees';

interface EmployeesTableProps {
  employees: Employee[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onRowClick: (employee: Employee) => void;
  onAcceptRequest: (employee: Employee) => void;
  onSendInvite: (employee: Employee) => void;
  onTogglePrimary: (employee: Employee, isPrimary: boolean) => void;
}



function getEmployeeId(employee: Employee): string {
  return employee.contact?.id || employee.user?.userId || employee.id;
}

function getEmployeeName(employee: Employee): string {
  return employee.contact?.name || employee.user?.username || '-';
}

export function EmployeesTable({
  employees,
  isLoading,
  selectedIds,
  onSelectionChange,
  onRowClick,
  onAcceptRequest,
  onSendInvite,
  onTogglePrimary,
}: EmployeesTableProps) {
  const { t } = useTranslation();
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const { data: roles } = useGlobalUserRoles();

  const roleMap = useMemo(() => {
    if (!roles) return new Map<string, string>();
    return new Map(roles.map((role) => [role.id, role.name]));
  }, [roles]);

  const allSelected =
    employees.length > 0 && selectedIds.size === employees.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(employees.map((e) => getEmployeeId(e))));
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(newSelection);
  };

  if (isLoading && employees.length === 0) {
    return (
      <TableLoadingView isLoading={isLoading} rows={5} columns={8} withHeader={true} />
    );
  }

  if (employees.length === 0) {
    return <EmptyTableView />;
  }

  return (
    <Table className="min-h-0 h-full" tableClassName="min-w-full divide-border">
      <TableHeader>
        <CheckboxHeader
          checked={allSelected}
          onChange={handleSelectAll}
          ariaLabel={t('employees.table.selectAll')}
          indeterminate={someSelected}
        />

        <NonSortableColumnHeader
          label={t('employees.table.columns.name')}
          minWidth="200px"
        />

        <NonSortableColumnHeader
          label={t('employees.table.columns.role')}
          minWidth="120px"
        />

        <NonSortableColumnHeader
          label={t('employees.table.columns.email')}
          minWidth="180px"
        />

        <NonSortableColumnHeader
          label={t('employees.table.columns.username')}
          minWidth="120px"
        />

        <NonSortableColumnHeader
          label={t('employees.table.columns.lastActive')}
          minWidth="120px"
        />

        <NonSortableColumnHeader
          label={t('employees.table.columns.invite')}
          minWidth="100px"
        />

        <NonSortableColumnHeader
          label={t('employees.table.columns.primaryContact')}
          minWidth="120px"
        />
      </TableHeader>

      <TableBody>
        {employees.map((employee) => {
          const employeeId = getEmployeeId(employee);
          const isDisabled = employee.contact?.disabled ?? false;

          return (
            <tr
              key={employeeId}
              className={`hover:bg-surface-darker/50 ${isDisabled ? 'opacity-50' : ''}`}
              onMouseEnter={() => setHoveredRowId(employeeId)}
              onMouseLeave={() => setHoveredRowId(null)}
            >
              <CheckboxCell
                checked={selectedIds.has(employeeId)}
                onChange={() => handleSelectRow(employeeId)}
                ariaLabel={t('employees.table.selectRow', { name: getEmployeeName(employee) })}
              />

              <TextWithHoverAction
                value={getEmployeeName(employee)}
                isHovered={hoveredRowId === employeeId}
                isEnabled={employee.isNew !== true}
                onCellClick={() => onRowClick(employee)}
                onActionClick={() => onRowClick(employee)}
                actionLabel={t('employees.table.open')}
              />

              <TextCell value={employee.contact?.role ? (roleMap.get(employee.contact.role) || employee.contact.role) : '-'} />

              <TextCell value={employee.contact?.email || employee.user?.userEmail || '-'} />

              <TextCell value={employee.user?.username || '-'} />

              <LastOnlineCell lastOnlineAt={employee.user?.lastActive} />

              <InviteLinkCell
                hasUser={!!employee.user}
                hasContact={!!employee.contact}
                hasActiveInvite={!!employee.invite}
                onSendInvite={() => onSendInvite(employee)}
                disabled={isDisabled}
                onAcceptJoinRequest={() => onAcceptRequest(employee)}
              />

              <ToggleCell
                checked={employee.contact?.isPrimary ?? false}
                onChange={(isPrimary) => onTogglePrimary(employee, isPrimary)}
                disabled={isDisabled || !employee.contact}
                ariaLabel={t('employees.table.togglePrimary', { name: getEmployeeName(employee) })}
              />
            </tr>
          );
        })}
      </TableBody>
    </Table>
  );
}
